import { ValueSetterParams } from '@ag-grid-community/core/dist/cjs/entities/colDef';
import { Box, Dialog, DialogContent, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TuneIcon from '@material-ui/icons/Tune';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingContainer from '../../../../../../components/LoadingContainer';
import DialogTitleBlock from '../../../../../../components/Modals/Dialog/DialogTitleBlock';
import TableGrid, { CellRenderer, MainColumns } from '../../../../../../components/TableGrid';
import { api } from '../../../../../../core/api';
import { getYearsFromRange } from '../../../../../../core/utils/year.utils';
import { BenchmarkCombinedRate } from '../../../../../../generated/model/benchmarkCombinedRate';
import { RangeOflong } from '../../../../../../generated/model/rangeOflong';
import { useErrorsDispatch } from '../../../../../FinancialData/shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../../FinancialData/shared/ErrorContext/ErrorContext.types';
import { useBenchmarkState } from '../../../../Context/BenchmarkContext';

interface OwnProps {
  show: boolean;
  yearRange: RangeOflong;
  handleClose: (hasChanges: boolean) => void;
}

export interface InterestRateRow {
  companyId: string;
  companyName: string;
  rates: BenchmarkCombinedRate[];
  [year: number]: number | undefined;
}

const InterestRatesModal: React.FC<OwnProps> = ({ show, yearRange, handleClose }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [rows, setRows] = useState<InterestRateRow[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { activeBenchmark } = useBenchmarkState();
  const dispatchErrors = useErrorsDispatch();

  const loadData = async () => {
    try {
      setIsLoading(true);
      const data = (await api.benchmark.fetchInterestRates(activeBenchmark?.id!)).map(
        (rateView) => {
          let row = {
            companyId: rateView.companyId!,
            companyName: rateView.companyName!,
            rates: rateView.rates!,
          };
          (rateView.rates || []).forEach((r) => {
            row = {
              ...row,
              [r.year!]: isDefined(r.companyRate) ? r.companyRate : r.countryRate,
            };
          });
          return row;
        },
      );
      setRows(data);
    } catch (error) {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (!show) {
        return;
      }
      await loadData();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, activeBenchmark, dispatchErrors]);

  const isDefined = (value: any): boolean => !!value || value === 0;

  const sameValues = (a: number | undefined, b: number | undefined): boolean =>
    a === b || (!isDefined(a) && !isDefined(b));

  const onValueChange = (params: ValueSetterParams, year: number) => {
    const storedRate = params.data.rates.filter(
      (rate: BenchmarkCombinedRate) => rate.year === year,
    )[0] as BenchmarkCombinedRate;
    const newVal = isDefined(params.newValue) ? Number(params.newValue) : undefined;
    if (newVal !== undefined && isNaN(newVal)) {
      return false;
    }
    if (
      !sameValues(storedRate?.companyRate, newVal) &&
      (isDefined(storedRate?.companyRate) || !sameValues(storedRate?.countryRate, newVal))
    ) {
      performSave(params.data.companyId, year, newVal);
      params.data[year.toString()] = isDefined(newVal) ? newVal : storedRate.countryRate;
      params.data.rates = params.data.rates.map((rate: BenchmarkCombinedRate) =>
        rate.year === year ? { ...rate, companyRate: newVal } : rate,
      );
      if (!params.data.rates.some((rate: BenchmarkCombinedRate) => rate.year === year)) {
        params.data.rates.push({ year, companyRate: newVal });
      }
    }
    return false;
  };

  const performSave = (companyId: string, year: number, rate?: number) => {
    (async () => {
      try {
        await api.benchmark.overrideInterestRate(activeBenchmark?.id!, { companyId, year, rate });
        setHasChanges(true);
      } catch (error) {
        dispatchErrors({
          type: ErrorContextActionTypes.SetError,
          payload: { error },
        });
        await loadData();
      }
    })();
  };

  const cols: MainColumns[] = [
    {
      field: 'companyName',
      minWidth: 300,
    },
    ...getYearsFromRange(yearRange).map((year) => ({
      field: year.toString(),
      headerName: year.toString(),
      minWidth: 85,
      unSortIcon: true,
      cellRenderer: CellRenderer.BenchmarkInterestRateCell,
      cellRendererParams: {
        year,
      },
      editable: true,
      valueSetter: (params: ValueSetterParams) => onValueChange(params, year),
    })),
  ];

  return (
    <Box>
      <Dialog
        fullWidth={true}
        maxWidth={false}
        disableRestoreFocus={true}
        disableBackdropClick={true}
        onClose={() => handleClose(hasChanges)}
        open={show}
      >
        <DialogTitleBlock
          icon={<TuneIcon />}
          title={t('benchmark:benchmarkResults.interestRates.modalTitle')}
          handleClose={() => handleClose(hasChanges)}
        />
        <DialogContent>
          {isLoading ? (
            <LoadingContainer />
          ) : (
            <>
              <Typography className={classes.typography}>
                {t('benchmark:benchmarkResults.interestRates.disclaimer')}
              </Typography>
              <Typography className={classes.typography}>
                {t('benchmark:benchmarkResults.interestRates.editInfo')}
              </Typography>
              <TableGrid
                columns={cols}
                rowSelection={false}
                isShowSideBar={false}
                translationKey={'benchmark:benchmarkResults.interestRates'}
                originalProps={{
                  rowData: rows,
                  domLayout: 'autoHeight',
                  rowModelType: 'clientSide',
                  singleClickEdit: true,
                  defaultColDef: {
                    resizable: true,
                    flex: 1,
                    menuTabs: [],
                    autoHeight: false,
                    wrapText: false,
                    sortable: false,
                  },
                }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const useStyles = makeStyles(() => ({
  typography: {
    fontWeight: 'bold',
  },
}));

export default InterestRatesModal;
